import React, { useLayoutEffect, useRef, useState } from 'react'
import type { ChartTooltipProps } from './ChartTooltip.types'
import './ChartTooltip.css'

const ChartTooltip: React.FC<ChartTooltipProps> = ({
  title,
  items,
  x,
  y,
  visible,
  darkMode = false,
  className = '',
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [flipY, setFlipY] = useState(false)
  const [offsetX, setOffsetX] = useState(0)

  useLayoutEffect(() => {
    if (!visible || !ref.current) return
    const node = ref.current
    const parent = node.offsetParent as HTMLElement | null
    if (!parent) return

    const parentWidth = parent.clientWidth
    const parentHeight = parent.clientHeight
    const tipWidth = node.offsetWidth
    const tipHeight = node.offsetHeight

    const half = tipWidth / 2
    let nextOffsetX = 0
    if (x - half < 4) nextOffsetX = half - x + 4
    else if (x + half > parentWidth - 4)
      nextOffsetX = parentWidth - 4 - (x + half)

    const prefersAbove = y - tipHeight - 12 >= 4
    setFlipY(!prefersAbove && y + tipHeight + 12 < parentHeight)
    setOffsetX(nextOffsetX)
  }, [visible, x, y, title, items])

  if (!visible || items.length === 0) return null

  const classNames = [
    'ds-chart-tooltip',
    flipY ? 'ds-chart-tooltip--flip-y' : '',
    darkMode ? 'ds-chart-tooltip--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={ref}
      className={classNames}
      style={{ left: x + offsetX, top: y }}
      role="tooltip"
    >
      {title ? <p className="ds-chart-tooltip__title">{title}</p> : null}
      <ul className="ds-chart-tooltip__list">
        {items.map((item) => (
          <li
            key={`${item.label}-${item.value}`}
            className="ds-chart-tooltip__item"
          >
            {item.color ? (
              <span
                className="ds-chart-tooltip__swatch"
                style={{ background: item.color }}
                aria-hidden="true"
              />
            ) : null}
            <span className="ds-chart-tooltip__label">{item.label}</span>
            <span className="ds-chart-tooltip__value">{item.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ChartTooltip
